import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useCart } from '../../contexts/CartContext';
import { useSettings } from '../../contexts/SettingsContext';
import supabase from '../../utils/supabaseClient';
import { cartConsultUrl } from '../../utils/whatsapp';
import './Cart.css';

function Cart({ lang = 'es' }) {
  const { 
    cart, 
    updateQuantity: originalUpdateQuantity, 
    removeFromCart, 
    clearCart,
    ivaData: contextIvaData,    // Get IVA data from context
    updateIvaData               // Get update method from context
  } = useCart();
  
  const { catalogueMode } = useSettings();

  const [localCart, setLocalCart] = useState(cart);
  const [ivaData, setIvaData] = useState(contextIvaData); // Initialize from context
  const [loading, setLoading] = useState(false);
  const [inventoryMap, setInventoryMap] = useState({});
  const [inventoryLoading, setInventoryLoading] = useState(false);
  
  const translations = {
    es: {
      emptyCart: "Tu carrito está vacío",
      continueShopping: "Continuar comprando",
      product: "Producto",
      price: "Precio",
      quantity: "Cantidad",
      total: "Total",
      remove: "Eliminar",
      subtotal: "Subtotal",
      checkout: "Proceder al pago",
      consultWhatsApp: "Enviar consulta por WhatsApp",
      clearCart: "Vaciar carrito",
      update: "Actualizar",
      retailPrice: "Precio Minorista",
      wholesalePrice: "Precio Mayorista (12+)",
      specialPrice: "Precio Especial (24+)",
    },
    en: {
      emptyCart: "Your cart is empty",
      continueShopping: "Continue shopping",
      product: "Product",
      price: "Price",
      quantity: "Quantity",
      total: "Total",
      remove: "Remove",
      subtotal: "Subtotal",
      checkout: "Proceed to checkout",
      consultWhatsApp: "Send inquiry via WhatsApp",
      clearCart: "Clear cart",
      update: "Update",
      retailPrice: "Retail Price",
      wholesalePrice: "Wholesale Price (12+)",
      specialPrice: "Special Price (24+)",
    }
  };
  
  const t = translations[lang] || translations.es;
  
  // Debounce function to limit quantity update frequency
  const debounce = (func, delay) => {
    let timer;
    return (...args) => {
      clearTimeout(timer);
      timer = setTimeout(() => {
        func(...args);
      }, delay);
    };
  };
  
  // Create a debounced version of updateQuantity
  const debouncedUpdateQuantity = useCallback(
    debounce((index, newQuantity) => {
      originalUpdateQuantity(index, newQuantity);
    }, 500), // 500ms delay
    [originalUpdateQuantity]
  );
  
  // Add a function to fetch inventory data for cart items
  const fetchInventoryData = async () => {
    if (localCart.length === 0) return;
    
    setInventoryLoading(true);
    
    try {
      const productIds = localCart.map(item => item.id).filter(Boolean);
      
      if (productIds.length === 0) {
        setInventoryLoading(false);
        return;
      }
      
      const { data: inventarioData, error } = await supabase
        .from('inventario')
        .select('producto_id, cantidad')
        .in('producto_id', productIds);
        
      if (error) {
        console.error('Error fetching inventory data:', error);
        return;
      }
      
      // Aggregate quantities for each product
      const inventoryMapData = {};
      inventarioData?.forEach(item => {
        if (!inventoryMapData[item.producto_id]) {
          inventoryMapData[item.producto_id] = 0;
        }
        inventoryMapData[item.producto_id] += item.cantidad;
      });
      
      setInventoryMap(inventoryMapData);
    } catch (err) {
      console.error('Error in inventory data fetch:', err);
    } finally {
      setInventoryLoading(false);
    }
  };
  
  // Update local cart when main cart changes
  useEffect(() => {
    setLocalCart(cart);
  }, [cart]);
  
  // Optimize IVA data fetching to only run when cart items or IVA IDs change
  useEffect(() => {
    const fetchIvaData = async () => {
      const ivaIds = [...new Set(localCart.filter(item => item.iva_id).map(item => item.iva_id))];
      
      // Check if we already have all the IVA data we need
      const missingIvaIds = ivaIds.filter(id => !ivaData[id]);
      
      if (missingIvaIds.length === 0) {
        return; // Skip fetching if we have all the data
      }
      
      setLoading(true);
      
      try {
        const { data, error } = await supabase
          .from('iva')
          .select('id, nombre, porcentaje, detalle')
          .in('id', ivaIds);
          
        if (error) {
          console.error('Error fetching IVA data:', error);
          return;
        }
        
        // Merge new IVA data with existing data
        const newIvaMap = { ...ivaData };
        data.forEach(iva => {
          newIvaMap[iva.id] = iva;
        });
        
        setIvaData(newIvaMap);
        updateIvaData(newIvaMap); // Update the IVA data in the context
      } catch (err) {
        console.error('Error in IVA data fetch:', err);
      } finally {
        setLoading(false);
      }
    };
    
    if (localCart.length > 0) {
      fetchIvaData();
    }
  }, [localCart.map(item => item.iva_id).join(',')]); // Only re-run when IVA IDs change
  
  // Update the getIvaPercentage function to convert decimal to percentage
  const getIvaPercentage = (item) => {
    if (!item.iva_id || !ivaData[item.iva_id]) {
      return 0;
    }
    // Multiply by 100 to convert decimal to percentage
    return (ivaData[item.iva_id].porcentaje || 0) * 100;
  };
  
  const getIvaName = (item) => {
    if (!item.iva_id || !ivaData[item.iva_id]) {
      return 'N/A';
    }
    return ivaData[item.iva_id].nombre || 'N/A';
  };
  
  // Fix the getBasePrice function - if price1 is 100 with 13% IVA, base price should be ~88.50
  const getBasePrice = (item) => {
    // First determine which price tier to use based on quantity
    let priceWithIVA;
    if (item.quantity >= 24 && item.precio3) {
      priceWithIVA = Number(item.precio3);
    } else if (item.quantity >= 12 && item.precio2) {
      priceWithIVA = Number(item.precio2);
    } else {
      priceWithIVA = Number(item.precio1);
    }
    
    // Then extract the base price by removing the IVA component
    const ivaPercentage = getIvaPercentage(item);
    if (ivaPercentage > 0) {
      // Formula: basePrice = priceWithIVA / (1 + (ivaPercentage/100))
      // For example: 100 / (1 + (13/100)) = 100 / 1.13 = 88.50
      return priceWithIVA / (1 + (ivaPercentage / 100));
    }
    
    return priceWithIVA; // If no IVA, price is already the base price
  };
  
  // Update the getPriceWithIVA function to simply return the selected price
  const getPriceWithIVA = (item) => {
    if (item.quantity >= 24 && item.precio3) {
      return Number(item.precio3);
    } else if (item.quantity >= 12 && item.precio2) {
      return Number(item.precio2);
    } else {
      return Number(item.precio1);
    }
  };
  
  // Fix the getIvaAmount function
  const getIvaAmount = (item) => {
    const priceWithIVA = getPriceWithIVA(item);
    const basePrice = getBasePrice(item);
    
    // Direct calculation: IVA amount = total price - base price
    // For example: 100 - 88.50 = 11.50
    return priceWithIVA - basePrice;
  };
  
  // Update the calculation functions
  const calculateSubtotal = () => {
    return localCart.reduce((total, item) => {
      const basePrice = getBasePrice(item);
      return total + (basePrice * item.quantity);
    }, 0);
  };

  const calculateTotalIVA = () => {
    return localCart.reduce((total, item) => {
      const ivaAmount = getIvaAmount(item);
      return total + (ivaAmount * item.quantity);
    }, 0);
  };

  const calculateTotal = () => {
    return localCart.reduce((total, item) => {
      const priceWithIVA = getPriceWithIVA(item);
      return total + (priceWithIVA * item.quantity);
    }, 0);
  };
  
  const cartTotal = localCart.reduce((total, item) => {
    return total + (getPriceWithIVA(item) * item.quantity);
  }, 0);
  
  const logIVAInfo = () => {
    console.log("IVA Information for Cart Items:");
    localCart.forEach((item, index) => {
      console.log(`Item ${index + 1}: ${item.nombre}`);
      console.log(`  IVA ID: ${item.iva_id || 'Not set'}`);
      console.log(`  IVA Percentage: ${getIvaPercentage(item)}%`);
      console.log(`  IVA Name: ${getIvaName(item)}`);
      console.log(`  Base Price: ₡${getBasePrice(item)}`);
      console.log(`  IVA Amount: ₡${getIvaAmount(item).toFixed(2)}`);
      console.log(`  Price with IVA: ₡${getPriceWithIVA(item).toFixed(2)}`);
      console.log('-------------------');
    });
  };

  useEffect(() => {
    if (!loading) {
      logIVAInfo();
    }
  }, [localCart, ivaData, loading]);

  // Add this function to determine the price label based on quantity
  const getPriceLabel = (item) => {
    if (item.quantity >= 24 && item.precio3) {
      return t.specialPrice;  // "Precio Especial (24+)"
    } else if (item.quantity >= 12 && item.precio2) {
      return t.wholesalePrice;  // "Precio Mayorista (12+)"
    } else {
      return t.retailPrice;  // "Precio Minorista"
    }
  };
  
  // Add effect to fetch inventory when cart changes
  useEffect(() => {
    if (localCart.length > 0) {
      fetchInventoryData();
    }
  }, [localCart.map(item => item.id).join(',')]);
  
  // Get available inventory for an item
  const getAvailableInventory = (item) => {
    if (!item || !item.id || !inventoryMap[item.id]) return 0;
    return inventoryMap[item.id];
  };
  
  // Check if quantity exceeds available inventory
  const isQuantityExceeded = (item) => {
    const available = getAvailableInventory(item);
    return available > 0 && item.quantity > available;
  };
  
  // Modify the updateLocalQuantity function to respect inventory limits
  const updateLocalQuantity = (index, newQuantity) => {
    if (newQuantity < 1) newQuantity = 1;
    
    const item = localCart[index];
    const availableInventory = getAvailableInventory(item);
    
    // Cap the quantity at available inventory if it's greater than 0
    if (availableInventory > 0 && newQuantity > availableInventory) {
      newQuantity = availableInventory;
    }
    
    // Update local cart state immediately for UI responsiveness
    const updatedCart = [...localCart];
    updatedCart[index].quantity = newQuantity;
    setLocalCart(updatedCart);
    
    // Debounce the actual cart update to prevent excessive reloads
    debouncedUpdateQuantity(index, newQuantity);
  };
  
  // Add a function to fetch color data if needed
  const fetchMissingColorData = async () => {
    // Find cart items that have color_numeric_id but no color_name
    const itemsNeedingColorData = localCart.filter(
      item => item.color_numeric_id && !item.color_name
    );
    
    if (itemsNeedingColorData.length === 0) return;
    
    const colorIds = [...new Set(itemsNeedingColorData.map(item => item.color_numeric_id))];
    
    try {
      const { data, error } = await supabase
        .from('color')
        .select('numeric_id, name, rgb_code')
        .in('numeric_id', colorIds);
        
      if (error) {
        console.error('Error fetching color data:', error);
        return;
      }
      
      // Create a color map for quick lookups
      const colorMap = {};
      data.forEach(color => {
        colorMap[color.numeric_id] = color;
      });
      
      // Update cart items with color names
      const updatedCart = [...localCart];
      let hasUpdates = false;
      
      updatedCart.forEach(item => {
        if (item.color_numeric_id && !item.color_name) {
          const colorData = colorMap[item.color_numeric_id];
          if (colorData) {
            item.color_name = colorData.name;
            item.color_rgb_code = colorData.rgb_code;
            hasUpdates = true;
          }
        }
      });
      
      if (hasUpdates) {
        setLocalCart(updatedCart);
      }
    } catch (err) {
      console.error('Error fetching color data:', err);
    }
  };

  // Call this function when the cart loads
  useEffect(() => {
    if (localCart.length > 0) {
      fetchMissingColorData();
    }
  }, [localCart.length]);
  
  if (localCart.length === 0) {
    return (
      <div className="empty-cart-container">
        <h2>{t.emptyCart}</h2>
        <Link to="/catalogue" className="continue-shopping-btn">
          {t.continueShopping}
        </Link>
      </div>
    );
  }
  
  if (loading && localCart.length > 0) {
    return <div className="loading">Loading cart information...</div>;
  }
  
  return (
    <div className="cart-page">
      <h1>Shopping Cart</h1>
      
      <div className="cart-container">
        <table className="cart-table">
          <thead>
            <tr>
              <th>{t.product}</th>
              {!catalogueMode && <th>{t.price}</th>}
              {!catalogueMode && <th>{t.total}</th>}
            </tr>
          </thead>
          <tbody>
            {localCart.map((item, index) => {
              const basePrice = getBasePrice(item);
              const priceWithIVA = getPriceWithIVA(item);
              const priceLabel = getPriceLabel(item);
              const ivaPercentage = getIvaPercentage(item);
              const ivaAmount = getIvaAmount(item);
              const availableInventory = getAvailableInventory(item);
              const isExceeded = isQuantityExceeded(item);
              
              return (
                <tr key={index} className={`cart-item ${isExceeded ? 'inventory-warning' : ''}`}>
                  <td className="cart-product-info">
                    <div className="cart-product-image">
                      <img src={item.imageUrl} alt={item.nombre} />
                    </div>
                    <div className="cart-product-details-fixed">
                      <div className="cart-product-title-row">
                        <span className="cart-product-title">{item.nombre}</span>
                        {item.codigo && <span className="cart-product-code">&nbsp;| Código: {item.codigo}</span>}
                      </div>
                      <div className="cart-product-attributes-row">
                        {item.color_name && <span>Color: {item.color_name}</span>}
                        {!item.color_name && item.color_numeric_id && <span>Color: ID #{item.color_numeric_id}</span>}
                        {item.talla_id && <span style={{marginLeft: 8}}>Talla: {item.talla_id}</span>}
                      </div>
                    </div>
                  </td>
                  <td className="cart-product-price">
                    {!catalogueMode && (
                      <div className="cart-product-price-content">
                        <div className="cart-product-price-main">
                          <div className="price-amount">₡{priceWithIVA.toFixed(2)}</div>
                          <div className="price-label">{priceLabel}</div>
                        </div>
                        <div className="price-breakdown">
                          <div>Base: ₡{basePrice.toFixed(2)}</div>
                          {ivaPercentage > 0 && (
                            <div>IVA ({ivaPercentage}%): ₡{ivaAmount.toFixed(2)}</div>
                          )}
                        </div>
                      </div>
                    )}
                    <div className="cart-product-quantity">
                      <div className="cart-product-quantity-middle">
                        <div className="quantity-controls">
                          <button 
                            onClick={() => updateLocalQuantity(index, item.quantity - 1)}
                            disabled={item.quantity <= 1}
                          >
                            -
                          </button>
                          <input 
                            type="number"
                            value={item.quantity}
                            onChange={(e) => {
                              const val = parseInt(e.target.value) || 1;
                              updateLocalQuantity(index, val);
                            }}
                            min="1"
                            max={availableInventory > 0 ? availableInventory : undefined}
                            className={`quantity-input ${isExceeded ? 'exceeded' : ''}`}
                          />
                          <button 
                            onClick={() => updateLocalQuantity(index, item.quantity + 1)}
                            disabled={availableInventory > 0 && item.quantity >= availableInventory}
                          >
                            +
                          </button>
                        </div>
                      </div>
                      <div className="cart-product-quantity-info">
                        {availableInventory > 0 ? (
                          <div className="inventory-info">
                            {availableInventory} disponibles
                          </div>
                        ) : (
                          <div className="inventory-warning">Agotado</div>
                        )}
                        {isExceeded && (
                          <div className="inventory-exceeded">
                            Ajustado
                          </div>
                        )}
                      </div>
                    </div>
                  </td>
                  {!catalogueMode && (
                    <td className="cart-product-subtotal">
                      <div className="cart-product-subtotal-amount">₡{(priceWithIVA * item.quantity).toFixed(2)}</div>
                    </td>
                  )}
                  <td className="cart-product-remove">
                    <button onClick={() => removeFromCart(index)} className="remove-item-btn">
                      {t.remove}
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        
        {/* Only show loading indicator when necessary */}
        {loading && <div className="loading-overlay">Updating prices...</div>}
        
        <div className="cart-summary">
          {!catalogueMode && (
            <>
              <div className="summary-row">
                <span>{t.subtotal}:</span>
                <span>₡{calculateSubtotal().toFixed(2)}</span>
              </div>
              <div className="summary-row">
                <span>IVA:</span>
                <span>₡{calculateTotalIVA().toFixed(2)}</span>
              </div>
              <div className="summary-row total-row">
                <span>{t.total}:</span>
                <span>₡{calculateTotal().toFixed(2)}</span>
              </div>
            </>
          )}
          <div className="summary-actions">
            <button onClick={clearCart} className="clear-cart-btn">
              {t.clearCart}
            </button>
            {catalogueMode ? (
              <button
                className="checkout-btn"
                onClick={() => window.open(cartConsultUrl(localCart), '_blank')}
              >
                {t.consultWhatsApp}
              </button>
            ) : (
              <Link
                to="/checkout"
                className={`checkout-btn ${localCart.some(isQuantityExceeded) ? 'disabled' : ''}`}
                onClick={(e) => {
                  if (localCart.some(isQuantityExceeded)) {
                    e.preventDefault();
                    alert('Por favor, ajuste las cantidades que exceden el inventario disponible antes de continuar.');
                  }
                }}
              >
                {t.checkout}
              </Link>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default Cart;
